import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { buildUserName, getLocalISODate } from "./utils";
import {
  showBookingErrorToast,
  showBookingInfoToast,
  showBookingSuccessToast,
} from "../components/bookingToast";
import UserBookingHeader from "./components/UserBookingHeader";
import UserFloorPlan from "./components/UserFloorPlan";
import UserSectionModal from "./components/UserSectionModal";
import MyBookings from "./components/MyBookings";
import WelcomeHeader from "./components/WelcomeHeader";
import { ConfirmDialog, PromptDialog } from "../components/Dialogs";
import { searchSeatsService } from "../../../../services/userBookings";

import { getCurrentUserProfileService } from "../../../../services/auth";

const getValidDefaultDate = () => {
  const d = new Date();
  if (d.getDay() === 6) d.setDate(d.getDate() + 2); // Sat -> Mon
  if (d.getDay() === 0) d.setDate(d.getDate() + 1); // Sun -> Mon
  return getLocalISODate(d);
};

const defaultDate = getValidDefaultDate();

const createBookingForm = (seatId, userName, date = null) => ({
  bookedFor: userName,
  dates: date ? [date] : [],
  seatId,
  requirement: "laptop",
  note: "",
  ignoreSeatTypeRule: false,
});

const UserBooking = () => {
  const dispatch = useDispatch();
  const workspace = useSelector((state) => state.userBookings.workspace);
  const availableSeats = useSelector((state) => state.userBookings.availableSeats);
  const blockedSeatIds = useSelector((state) => state.userBookings.blockedSeatIds);
  const reservedSeatIds = useSelector((state) => state.userBookings.reservedSeatIds);
  const permanentSeatIds = useSelector((state) => state.userBookings.permanentSeatIds);
  const recurringSeatIds = useSelector((state) => state.userBookings.recurringSeatIds);
  const loading = useSelector((state) => state.userBookings.loading);
  const userInfo = useSelector((state) => state.auth.userInfo);

  const userName = useMemo(() => buildUserName(userInfo), [userInfo]);
  const currentUserId = useMemo(() => {
    const inner = userInfo?.userInfo || userInfo;
    return inner?._id;
  }, [userInfo]);

  const [selectedDate, setSelectedDate] = useState(null);
  const [requirement, setRequirement] = useState("laptop");
  const bookingHeaderRef = useRef(null);

  const handleBookNow = () => {
    bookingHeaderRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  const [selectedSectionId, setSelectedSectionId] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedSeatId, setSelectedSeatId] = useState(null);
  const [bookingForm, setBookingForm] = useState(() => createBookingForm("", userName));
  const [userSeatCapability, setUserSeatCapability] = useState(null);

  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false });
  const [promptDialog, setPromptDialog] = useState({ isOpen: false });

  useEffect(() => {
    const fetchWorkspace = () => {
      dispatch.userBookings.loadWorkspace().catch((err) => {
        toast.error(err.message || "Unable to load office layout.");
      });
    };

    const fetchProfile = async () => {
      try {
        const response = await getCurrentUserProfileService();
        const profileData = response?.data?.data || response?.data || response || {};
        const cap = (
          profileData?.seat_capability ||
          profileData?.user?.seat_capability ||
          profileData?.userInfo?.seat_capability ||
          profileData?.device ||
          profileData?.user?.device ||
          userInfo?.seat_capability ||
          userInfo?.userInfo?.seat_capability ||
          userInfo?.device ||
          userInfo?.userInfo?.device
        );
        const capability = cap?.toLowerCase();
        
        if (capability) {
          setUserSeatCapability(capability);
        }
      } catch (err) {
        console.log("Error fetching profile", err);
        // Fallback to redux
        const fallbackCap = (
          userInfo?.seat_capability ||
          userInfo?.userInfo?.seat_capability ||
          userInfo?.device ||
          userInfo?.userInfo?.device
        )?.toLowerCase();
        if (fallbackCap) setUserSeatCapability(fallbackCap);
      }
    };

    fetchWorkspace();
    fetchProfile();

    const handleStorageChange = (e) => {
      if (e.key === "tblr-admin-seat-booking-workspace-v2") {
        fetchWorkspace();
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, [dispatch]);

  // Keep bookedFor in sync if userName resolves after mount
  useEffect(() => {
    setBookingForm((prev) => ({ ...prev, bookedFor: userName }));
  }, [userName]);

  // Keep requirement in form in sync with top-level toggle
  useEffect(() => {
    setBookingForm((prev) => ({ ...prev, requirement }));
  }, [requirement]);

  // Keep booking date in sync with header date selector
  useEffect(() => {
    setBookingForm((prev) => ({ ...prev, dates: selectedDate ? [selectedDate] : [] }));
  }, [selectedDate]);

  useEffect(() => {
    if (workspace.layoutId && selectedDate) {
      dispatch.userBookings.loadAvailableSeats({ date: selectedDate });
    } else if (!selectedDate) {
      dispatch.userBookings.setAvailableSeats(null);
    }
  }, [dispatch, selectedDate, workspace.layoutId, workspace.updatedAt]);

  const office = workspace.office || { name: "Office", gridColumns: 12, gridRows: 10 };
  const sections = useMemo(() => workspace.sections || [], [workspace.sections]);
  const allBookings = useMemo(() => workspace.bookings || [], [workspace.bookings]);
  
  const myBookings = useMemo(
    () =>
      allBookings.filter(
        (b) =>
          (b.bookedFor === currentUserId || b.bookedFor === userName) &&
          (b.status === "active" || b.status === "reserved")
      ),
    [allBookings, currentUserId, userName]
  );

  const hasExistingBooking = useMemo(() => {
    return myBookings.some((b) => {
      if (b.date === selectedDate) return true;
      if (b.bookingType === "permanent" || b.date === "permanent") {
        if (permanentSeatIds && permanentSeatIds.includes(b.seatId)) {
          return true;
        }
      }
      return false;
    });
  }, [myBookings, selectedDate, permanentSeatIds]);

  const previousIsLaptopFull = useRef(false);

  const computedIsLaptopFull = useMemo(() => {
    // Wait until a date is selected before evaluating availability rules
    if (!selectedDate) {
      previousIsLaptopFull.current = false;
      return false;
    }

    if (loading) return previousIsLaptopFull.current;

    if (hasExistingBooking) {
      previousIsLaptopFull.current = false;
      return false;
    }
    
    if (!availableSeats) {
      previousIsLaptopFull.current = false;
      return false;
    }
    
    const allSeats = sections.flatMap(s => s.seats || []);
    const hasAvailableLaptop = allSeats.some(seat => seat.type === "laptop" && availableSeats.includes(seat.id));
    
    const isFull = !hasAvailableLaptop;
    previousIsLaptopFull.current = isFull;
    return isFull;
  }, [selectedDate, availableSeats, sections, hasExistingBooking, loading]);

  useEffect(() => {
    // Don't set requirement until capability is resolved from API
    if (!userSeatCapability) return;

    if (userSeatCapability === "laptop") {
      if (computedIsLaptopFull) {
        setRequirement("monitor");
      } else {
        setRequirement("laptop");
      }
    } else {
      // Monitor / Desktop users are locked to their own type
      setRequirement(userSeatCapability);
    }
  }, [userSeatCapability, computedIsLaptopFull]);

  const selectedSection = useMemo(
    () => sections.find((s) => s.id === selectedSectionId) || null,
    [sections, selectedSectionId]
  );

  const handleSectionClick = (section) => {
    if (!selectedDate) {
      toast.error("Please select a date first to view seat availability.");
      bookingHeaderRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      return;
    }

    setSelectedSectionId(section.id);
    setSelectedSeatId(null);
    setBookingForm({ ...createBookingForm("", userName, selectedDate), requirement });
    setIsModalOpen(true);
    // Fetch latest availability immediately when opening section
    dispatch.userBookings.loadAvailableSeats({ date: selectedDate });
  };

  const handleSeatSelect = (seatId) => {
    // One seat at a time — deselect if clicking same seat
    if (seatId === selectedSeatId) {
      setSelectedSeatId(null);
      setBookingForm((prev) => ({ ...prev, seatId: "" }));
    } else {
      setSelectedSeatId(seatId);
      setBookingForm((prev) => ({ ...prev, seatId }));
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!bookingForm.seatId) {
      toast.error("Select a seat first.");
      return;
    }

    const date = bookingForm.dates[0];
    if (!date) {
      toast.error("No date selected.");
      return;
    }

    if (userSeatCapability === "laptop") {
      if (!computedIsLaptopFull && requirement !== "laptop") {
        toast.error("Laptop users must book a Laptop seat unless they are all full.");
        return;
      }
      if (computedIsLaptopFull && requirement === "laptop") {
        toast.error("Laptop seats are full, try booking from Desktop or Monitor.");
        return;
      }
    }

    if (userSeatCapability === "monitor" && requirement !== "monitor") {
      toast.error("Monitor users can only book Monitor seats.");
      return;
    }
    
    if (userSeatCapability === "desktop" && requirement !== "desktop") {
      toast.error("Desktop users can only book Desktop seats.");
      return;
    }

    // Final check for existing booking on this date
    const hasExisting = myBookings.some((b) => {
      if (b.date === date) return true;
      if (b.bookingType === "permanent" || b.date === "permanent") {
        if (permanentSeatIds && permanentSeatIds.includes(b.seatId)) {
          return true;
        }
      }
      return false;
    });
    if (hasExisting) {
      toast.error(`You already have a seat reserved for ${date}.`);
      return;
    }

    try {
      await dispatch.userBookings.createMultipleBookings({
        ...bookingForm,
        dates: [date],
        bookingType: "one-time",
      });
      showBookingSuccessToast("Seat reserved successfully.");
      setSelectedSeatId(null);
      setSelectedDate(null);
      setBookingForm({ ...createBookingForm("", userName, null), requirement });
      setIsModalOpen(false);
    } catch (error) {
      showBookingErrorToast(error.message || "Unable to reserve seat.");
    }
  };

  const handleCancel = (bookingId) => {
    setPromptDialog({
      isOpen: true,
      title: "Cancel Booking",
      message: "Reason for cancellation (optional):",
      defaultValue: "",
      isDestructive: true,
      onConfirm: async (reason) => {
        setPromptDialog({ isOpen: false });
        try {
          await dispatch.userBookings.cancelBooking({
            bookingId,
            reason: reason || "Cancelled by user.",
          });
          showBookingSuccessToast("Booking cancelled.");
        } catch (error) {
          showBookingErrorToast(error.message || "Unable to cancel booking.");
        }
      },
      onCancel: () => setPromptDialog({ isOpen: false }),
    });
  };

  const handleCancelAllRecurring = async (seatId, bookedFor) => {
    const futureDates = allBookings.filter(
      (b) =>
        b.status === "active" &&
        b.seatId === seatId &&
        b.bookedFor === bookedFor &&
        b.bookingType === "recurring" &&
        b.date >= defaultDate
    );

    if (futureDates.length === 0) {
      showBookingInfoToast("No upcoming recurring bookings to cancel.");
      return;
    }

    setConfirmDialog({
      isOpen: true,
      title: "Cancel Recurring Bookings",
      message: `Cancel all ${futureDates.length} upcoming recurring bookings for this seat?`,
      isDestructive: true,
      onConfirm: () => {
        setConfirmDialog({ isOpen: false });
        setPromptDialog({
          isOpen: true,
          title: "Cancellation Reason",
          message: "Reason (optional):",
          defaultValue: "",
          isDestructive: true,
          onConfirm: async (reason) => {
            setPromptDialog({ isOpen: false });
            try {
              await dispatch.userBookings.cancelMultipleBookings({
                bookingIds: futureDates.map((b) => b.id),
                reason: reason || "Recurring cancelled by user.",
              });
              showBookingSuccessToast(`${futureDates.length} recurring bookings cancelled.`);
            } catch (error) {
              showBookingErrorToast(error.message || "Unable to cancel bookings.");
            }
          },
          onCancel: () => setPromptDialog({ isOpen: false }),
        });
      },
      onCancel: () => setConfirmDialog({ isOpen: false }),
    });
  };

  // Check today's seat availability for permanent booking cancellation detection
  const todayStr = getLocalISODate(new Date());
  const [todayPermanentSeatIds, setTodayPermanentSeatIds] = useState(null);

  useEffect(() => {
    const checkTodayAvailability = async () => {
      const layoutId = workspace.layoutId;
      if (!layoutId) return;
      try {
        const startTime = new Date(`${todayStr}T00:00:00`).toISOString();
        const endTime = new Date(`${todayStr}T23:59:59`).toISOString();
        const response = await searchSeatsService({
          layout_id: layoutId,
          start_time: startTime,
          end_time: endTime,
        });
        const sectionData = response.data?.data || response.data || [];
        const permIds = [];
        if (Array.isArray(sectionData)) {
          sectionData.forEach((sec) => {
            (sec.seats || []).forEach((s) => {
              if (s._id && s.booking_details?.type?.toLowerCase() === "permanent") {
                permIds.push(s._id);
              }
            });
          });
        }
        setTodayPermanentSeatIds(permIds);
      } catch (err) {
        console.error("Error checking today seat availability:", err);
      }
    };
    checkTodayAvailability();
  }, [workspace.layoutId, todayStr]);

  const stats = useMemo(() => {
    // 1. Check for a specific booking for today
    const specificBooking = myBookings.find((b) => b.date === todayStr);
    let todayBooking = specificBooking || null;

    // 2. If no specific booking, check permanent — but verify not cancelled for today
    if (!todayBooking) {
      const permanentBooking = myBookings.find(
        (b) => b.bookingType === "permanent" || b.date === "permanent"
      );
      if (permanentBooking) {
        if (
          todayPermanentSeatIds !== null &&
          !todayPermanentSeatIds.includes(permanentBooking.seatId)
        ) {
          todayBooking = null; // cancelled for today
        } else {
          todayBooking = permanentBooking;
        }
      }
    }

    const todaySection = todayBooking ? sections.find((s) => s.id === todayBooking.sectionId) : null;
    const todaySeat = todaySection ? todaySection.seats.find((s) => s.id === todayBooking.seatId) : null;

    const uniqueSections = new Set(myBookings.map((b) => b.sectionId)).size;

    return {
      todayBooking: todayBooking
        ? {
            seatLabel: todayBooking.seatNumber || todaySeat?.label || "—",
            sectionName: todaySection?.name || "—",
            time: todayBooking.bookingType === "permanent" ? "Always Active" : "9:00 - 18:00",
          }
        : null,
      activeCount: myBookings.length,
      sectionCount: uniqueSections,
    };
  }, [myBookings, sections, todayStr, todayPermanentSeatIds]);

  return (
    <div className="container mx-auto pt-8 pb-20">
      <h1 className="text-[#252525] text-3xl font-light leading-4 pb-8">
        Seat Booking
      </h1>
      {/* 1. Welcome Section */}
      <WelcomeHeader 
        userName={userName} 
        stats={stats}
        onReserveClick={handleBookNow}
      />

      {/* 2. User Bookings Section */}
      <MyBookings
        myBookings={myBookings}
        sections={sections}
        onCancel={handleCancel}
        onCancelAllRecurring={handleCancelAllRecurring}
      />

      {/* 3. Configuration Header (Date & Hardware) */}
      <div ref={bookingHeaderRef}>
      <UserBookingHeader
        selectedDate={selectedDate}
        setSelectedDate={setSelectedDate}
        requirement={requirement}
        setRequirement={setRequirement}
        userSeatCapability={userSeatCapability}
        isLaptopFull={computedIsLaptopFull}
      />
      </div>

      <div className="mb-8" /> {/* Small gap */}

      {/* 4. Floor Layout Section */}
      <div>
        <UserFloorPlan
          office={office}
          sections={sections}
          bookings={allBookings}
          selectedDate={selectedDate}
          requirement={requirement}
          selectedSectionId={selectedSectionId}
          onSectionClick={handleSectionClick}
          availableSeats={availableSeats}
          blockedSeatIds={blockedSeatIds}
          reservedSeatIds={reservedSeatIds}
          permanentSeatIds={permanentSeatIds}
          recurringSeatIds={recurringSeatIds}
        />
      </div>

      {isModalOpen && selectedSection && (
        <UserSectionModal
          section={selectedSection}
          allBookings={allBookings}
          availableSeats={availableSeats}
          blockedSeatIds={blockedSeatIds}
          reservedSeatIds={reservedSeatIds}
          permanentSeatIds={permanentSeatIds}
          recurringSeatIds={recurringSeatIds}
          selectedDate={selectedDate}
          requirement={requirement}
          currentUser={{ id: currentUserId, name: userName }}
          selectedSeatId={selectedSeatId}
          onSeatSelect={handleSeatSelect}
          bookingForm={bookingForm}
          onSubmit={handleSubmit}
          onClose={() => setIsModalOpen(false)}
          hasExistingBooking={hasExistingBooking}
        />
      )}

      <ConfirmDialog {...confirmDialog} />
      <PromptDialog {...promptDialog} />
    </div>
  );
};

export default UserBooking;
